import React, { useRef, useEffect } from 'react';
import './Intro.scss';
import { gsap } from '../../gsap';
import Hero from './Hero/Hero';
import CommonCopy from '../Common/CopyBlocks/CommonCopy';
import CTA from '../Common/CTA/CTA';
import Carousel from './Carousel/Carousel';
import Hyperlink from '../Common/Hyperlink/Hyperlink';

function Intro({
  navBarRef,
  handleMouseEnterTooltip,
  handleMouseLeaveTooltip,
  currentEra,
  setCurrentEra,
  screenWidth,
  showArrow
}) {
  const copyRef1 = useRef();
  const copyRef2 = useRef();
  const copyRef3 = useRef();
  const titleRef = useRef();
  const msRef = useRef();
  const videoLoopRef = useRef();
  const logoLockRef = useRef();

  const copyContents = {
    copy1: [
      'In 1969, the world watched the Apollo astronauts walk on the moon. With those “small” steps, the astronaut became a symbol of humankind’s determination to explore new paths.',
      'Today it’s no longer enough for the lone astronaut in his spacesuit to represent progress — we must create space for everyone.'
    ],
    copy2: [
      'Space is rapidly changing from the “final frontier” to a new economy of ideas, growth, and untapped opportunities.'
    ]
  };

  const hyperlinkData = {
    data1: {
      copy: 'trillion dollars by 2040',
      href: 'https://www.morganstanley.com/Themes/global-space-economy/?cid=mg-cap-cnncreat-12577'
    },
    data2: {
      href: 'https://www.morganstanley.com/articles/gender-diversity?cid=mg-cap-cnncreat-12578',
      copy: 'more effective, inclusive workplace'
    }
  };

  useEffect(() => {
    const tl = gsap.timeline({
      delay: 0.5,
      once: true
    });

    tl.to(logoLockRef.current, { opacity: 1, duration: 1.5, scale: 1, ease: 'ease-in' })
      .to(msRef.current, { opacity: 1, duration: 1.5, scale: 1, ease: 'ease-in' }, '+=.5')
      .to(titleRef.current, {
        duration: 1,
        opacity: 1,
        ease: 'ease-in'
      })
      .to(navBarRef.current, { opacity: 1, duration: 1, ease: 'ease-in' }, '-=0.5');

    videoLoopRef.current.play();
  }, [navBarRef]);

  return (
    <section className="intro">
      <Hero
        titleRef={titleRef}
        videoLoopRef={videoLoopRef}
        logoLockRef={logoLockRef}
        msRef={msRef}
        screenWidth={screenWidth}
      />
      <div className="intro__copy">
        <CommonCopy copyContents={copyContents.copy1} copyRef={copyRef1} id="intro" />
      </div>
      <Carousel showArrow={showArrow} currentEra={currentEra} setCurrentEra={setCurrentEra} />
      <div className="intro__copy">
        <CommonCopy copyContents={copyContents.copy2} copyRef={copyRef2} />

        <div className="intro__copy__line" />
        <svg
          className="intro__copy__quotationMark"
          viewBox="0 0 46 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.2783 15.7381C19.8372 15.7381 20.2929 16.1932 20.2929 16.7512L20.2929 34.9869C20.2929 35.5449 19.8371 36 19.2783 36L1.01465 36C0.455784 36 3.77491e-06 35.5449 3.72613e-06 34.9869L2.13191e-06 16.7512C2.08313e-06 16.1931 0.233847 15.3463 0.517218 14.8674L8.87791 0.826377C9.16328 0.347532 9.85293 -0.0244707 10.4098 0.00126196L15.3027 0.224856C15.8595 0.250579 16.2123 0.715577 16.0855 1.25776L12.9285 14.7527C12.8017 15.2949 13.1545 15.7381 13.7133 15.7381L19.2783 15.7381Z"
            fill="white"
          />
          <path
            d="M44.9854 15.7381C45.5442 15.7381 46 16.1932 46 16.7512L46 34.9869C46 35.5449 45.5442 36 44.9854 36L26.7217 36C26.1628 36 25.7071 35.5449 25.7071 34.9869L25.7071 16.7512C25.7071 16.1931 25.9409 15.3463 26.2262 14.8674L34.5834 0.826375C34.8688 0.34753 35.5584 -0.0244729 36.1153 0.00125971L41.0081 0.224854C41.565 0.250577 41.9178 0.715575 41.7909 1.25776L38.634 14.7527C38.5072 15.2949 38.8599 15.7381 39.4188 15.7381L44.9854 15.7381Z"
            fill="white"
          />
        </svg>
        <blockquote className="intro__copy__quote">
          The space economy could be worth a{' '}
          <span className="intro__copy__quote--bold">
            <Hyperlink hyperlinkData={hyperlinkData.data1} />
          </span>
          .”
        </blockquote>
        <p className="intro__copy__quoteBy intro__copy__quoteBy-mobile">
          KRISTINE LIWAG – Head of Aerospace and Defense Research at Morgan Stanley
        </p>
        <p className="intro__copy__quoteBy intro__copy__quoteBy-desktop">
          <span className="intro__copy__quoteBy--bold">KRISTINE LIWAG</span>
          <br />
          Head of Aerospace and Defense Research at Morgan Stanley
        </p>

        <div className="commonCopy" ref={copyRef3}>
          <div className="commonCopy__contents">
            <p className="commonCopy__contents__paragraphs">
              In order for this vision of the space economy to be realized, we must create access
              for as many qualified people as possible to participate.
            </p>
            <p className="commonCopy__contents__paragraphs">
              This starts with next-generation spacesuits that address the needs of all astronauts,
              regardless of size or gender. Once custom-made for all-male astronaut crews, today
              NASA and the private sector are working together to design suits that balance fit,
              flexibility, and reusability — enabling space to become, in its own way, a{' '}
              <strong className="commonCopy__contents__paragraphs--link">
                <Hyperlink hyperlinkData={hyperlinkData.data2} />
              </strong>
              .
            </p>
            <p className="commonCopy__contents__paragraphs">
              Inspired by this mission, Morgan Stanley has imagined its own spacesuit,{' '}
              <strong className="commonCopy__contents__paragraphs--link">
                <a className="hyperlink" href="#theFuture">
                  the Access I
                </a>
              </strong>
              , a conceptual model spacesuit intended to fit everyone.
            </p>
            <strong className="commonCopy__contents__paragraphs--bold">
              A more inclusive tomorrow starts today.
            </strong>
          </div>
        </div>
        <CTA
          handleMouseEnterTooltip={handleMouseEnterTooltip}
          handleMouseLeaveTooltip={handleMouseLeaveTooltip}
          origin="intro"
        />
      </div>
    </section>
  );
}

export default Intro;
